/**
 * @module components/analysis/SimilarWorksSection
 * @description
 *   "Similar Artists" / "Similar Books" card — chips linking out to a search
 *   for each comparable work, gated behind a paywall lock overlay for
 *   non-premium viewers.
 *
 * @extracted-from client/src/App.js (lines ~2666–2731, Phase 7)
 */

import React, { useState } from 'react';
import { Users } from 'lucide-react';
import analytics from '../../utils/analytics';
import { Card, SectionHeader, LockOverlay, Chip } from '../ui';

/**
 * @param {object} props
 * @param {{ similarArtists?: string[] }} props.results
 * @param {'music'|'book'} props.craftType
 * @param {boolean} props.showPremiumContent
 * @param {(item: string) => void} [props.onItemClick]
 */
export default function SimilarWorksSection({ results, craftType, showPremiumContent, onItemClick }) {
  const [sectionCollapsed, setSectionCollapsed] = useState(false);

  if (!results.similarArtists || results.similarArtists.length === 0) {
    return null;
  }

  const handleItemClick = (item) => {
    if (onItemClick) {
      onItemClick(item);
      return;
    }
    analytics.trackSimilarArtistClick(item);
    window.open(`https://www.google.com/search?q=${encodeURIComponent(item)}`, '_blank');
  };

  return (
    <Card className="animate-slide-up glow-effect-blue hover-lift relative">
      {!showPremiumContent && !sectionCollapsed && (
        <LockOverlay
          rounded
          tone="indigo"
          title={`Unlock ${craftType === 'music' ? 'similar artists' : 'similar books'}`}
        />
      )}
      <SectionHeader
        collapsed={sectionCollapsed}
        onToggleCollapse={() => setSectionCollapsed((c) => !c)}
        icon={<Users className="h-6 w-6 text-white" />}
        gradient="indigo"
        title={craftType === 'music' ? 'Similar Artists' : 'Similar Books'}
        subtitle={
          craftType === 'music'
            ? 'Artists with a comparable sound and market lane.'
            : 'Titles with a comparable voice and positioning.'
        }
      />
      {!sectionCollapsed ? (
      <div className="grid grid-cols-2 sm:flex sm:flex-wrap gap-3 sm:gap-4">
        {showPremiumContent
          ? results.similarArtists.map((item, index) => (
              <Chip
                key={index}
                variant="indigo"
                interactive
                className="animate-slide-up text-center"
                style={{ animationDelay: `${index * 100}ms` }}
                onClick={() => handleItemClick(item)}
              >
                {item}
              </Chip>
            ))
          : Array.from({ length: 6 }).map((_, index) => (
              <Chip key={index} variant="indigo" locked className="text-center">
                ---
              </Chip>
            ))}
      </div>
      ) : null}
    </Card>
  );
}
